import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { formatDate, formatPrice } from '@utils/format';
import { ResultsType } from '@types';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { useData } from '@hooks/useData';
import { useFilters } from '@hooks/useFilters';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { Pagination } from '@components/molecules/Pagination';
import { SITE_URLS } from '@constants';
import { GetMyProductReservationsSuccessResponse, useGetMyProductReservations } from '@services/useGetMyProductReservations';

export const MyProductReservations: FC = () => {
    const { pageToRequest, checkPageToRequest, observerRef: scrollObserverRef } = useInfiniteScroll();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('my-product-reservations-component.title'),
    };

    const { request: requestMyProductReservations } = useGetMyProductReservations();
    const { myProductReservations, isLoadingMyProductReservations, setMyProductReservations } = useData();
    const { results, setResults, currentPage, setCurrentPage } = useFilters();

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setMyProductReservations(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchProductReservations = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (myProductReservations?.pagination?.total_pages && page > myProductReservations?.pagination?.total_pages) {
            return;
        }

        requestMyProductReservations({ page: page });
    };

    // request more reservations from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingMyProductReservations && fetchProductReservations(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest, isLoadingMyProductReservations]);

    useEffect(() => {
        fetchProductReservations(1);
    }, [i18n.language]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setResults((previous: any[]) => {
            return myProductReservations?.pagination?.current_page === 1
                ? myProductReservations?.data || []
                : [...previous, ...(myProductReservations?.data || [])];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myProductReservations]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4">
                    <ReservationsTable />
                    <div ref={scrollObserverRef} style={{ height: '1px' }} />
                </div>
            </>
        </MyAccountContainer>
    );
};

const ReservationsTable: FC = () => {
    const { results: productReservations } = useFilters();
    const { isLoadingMyProductReservations } = useData();
    const { t } = useTranslation();

    const translations = {
        product: t('my-product-reservations-component.table.product'),
        date: t('my-product-reservations-component.table.date'),
        quantity: t('my-product-reservations-component.table.quantity'),
        total: t('my-product-reservations-component.table.total'),
        store: t('my-product-reservations-component.table.store'),
        actions: t('my-product-reservations-component.table.actions'),
    };

    return (
        <div>
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        { classes: 'text-start', text: translations.product },
                        { classes: 'text-center', hideOnMobile: true, text: translations.quantity },
                        { classes: 'text-end', hideOnMobile: true, text: translations.total },
                        { classes: 'text-start', hideOnMobile: true, text: translations.store },
                        { classes: 'text-center', hideOnMobile: true, text: translations.date },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={((productReservations as GetMyProductReservationsSuccessResponse['data']) || []).map(
                        (reservation) => {
                            return [
                                {
                                    classes: 'text-start',
                                    content: <Typography fontSize="14">{`${reservation.product_name}`}</Typography>,
                                },
                                {
                                    classes: 'text-center',
                                    content: <Typography fontSize="14">{`${reservation.quantity}`}</Typography>,
                                },
                                {
                                    classes: 'text-end',
                                    content: <Typography fontSize="14">{formatPrice(reservation.total)}</Typography>,
                                },
                                {
                                    classes: 'text-start',
                                    content: (
                                        <Anchor
                                            href={SITE_URLS.merchantDetail.replace(':uuid', `${reservation.merchant?.uuid}`)}
                                            mode={AnchorMode.none}>
                                            <Typography fontSize="14">{reservation.merchant?.name}</Typography>
                                        </Anchor>
                                    ),
                                },
                                {
                                    classes: 'text-center',
                                    content: (
                                        <Typography fontSize="14">
                                            {reservation.created_at && formatDate(reservation.created_at)}
                                        </Typography>
                                    ),
                                },
                                {
                                    classes: 'text-end',
                                    content: (
                                        <Table.Options
                                            options={[
                                                <Anchor
                                                    href={SITE_URLS.myProductReservationDetail.replace(
                                                        ':uuid',
                                                        `${reservation.uuid}`
                                                    )}
                                                    mode={AnchorMode.none}>
                                                    <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                        <i className="fa-light fa-eye" />
                                                    </Typography>
                                                </Anchor>,
                                            ]}
                                        />
                                    ),
                                },
                            ];
                        }
                    )}
                />
            </Card>
            <Pagination loading={isLoadingMyProductReservations} type={ResultsType.reservations} />
        </div>
    );
};
